import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create a dedicated axios instance for auth - no interceptors that could interfere
const authApi = axios.create();

// Token storage in sessionStorage (session-scoped, cleared on tab close)
const getToken = () => {
    try { return sessionStorage.getItem('gstore_token'); } 
    catch (e) { return null; }
};
const setTokenStorage = (token) => {
    try { sessionStorage.setItem('gstore_token', token); } 
    catch (e) { /* storage unavailable */ }
};
const clearTokenStorage = () => {
    try { sessionStorage.removeItem('gstore_token'); } 
    catch (e) { /* storage unavailable */ }
};

// Setup a SINGLE global interceptor - add Bearer token to all requests
const interceptorId = axios.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    // Remove withCredentials - we use only Bearer token, no cookies
    return config;
});

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const initialCheckDone = useRef(false);

    const checkAuth = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/api/auth/me`);
            setUser(response.data);
        } catch (e) {
            clearTokenStorage();
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!initialCheckDone.current) {
            initialCheckDone.current = true;
            checkAuth();
        }
    }, [checkAuth]);

    const login = async (email, password) => {
        // Use clean axios instance for login - no interceptor interference
        const response = await authApi.post(`${API}/api/auth/login`, { email, password });
        const data = response.data;
        if (data.access_token) {
            setTokenStorage(data.access_token);
        }
        setUser(data);
        return data;
    };

    const register = async (email, password, name, phone) => {
        const response = await authApi.post(`${API}/api/auth/register`, { email, password, name, phone });
        const data = response.data;
        if (data.access_token) {
            setTokenStorage(data.access_token);
        }
        setUser(data);
        return data;
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/api/auth/logout`);
        } catch (e) {
            // Ignore - we clear local state anyway
        }
        clearTokenStorage();
        setUser(null);
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        checkAuth,
        isAdmin: user?.role === 'admin'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
